import { client } from "@/hono";
import { ColumnForm, useOrderFileImport } from "@/hooks/use-order-file-import";
import { useOrderTemplates } from "@/hooks/use-order-templates";
import { SelectArticlesModal } from "@/modals/stock/select-articles-modal";
import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Menu } from "@msuite/ui/menu";
import { ConfirmModal } from "@msuite/ui/modal";
import { toast } from "@msuite/ui/toast";
import { Fragment, responseHandler } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import type { OrderFormSchema } from "@msuite/utils/schemas";
import { mergeArticleIds } from "@msuite/utils/stock";
import { useFormContext } from "react-hook-form";

export function OrderFormArticlesMenu() {
	/** Hooks */
	const disclosure = useDisclosure();
	const { t } = useTranslation();
	const { orderTemplates } = useOrderTemplates();
	const methods = useFormContext<OrderFormSchema>();

	/** Import */
	const {
		onOpen: handleOnOpenFileImport,
		dropzone,
		columnFormController,
		columnFormMethods,
		importErrorController,
	} = useOrderFileImport((items, fileId) => {
		methods.setValue("articles", mergeArticleIds(methods.getValues("articles"), items));
		const currentOrderFileIds = methods.getValues("orderFileIds") || [];
		methods.setValue("orderFileIds", currentOrderFileIds.concat([fileId]));
	});

	/** Functions */
	async function handleOnSelectOrderTemplate(orderTemplateId: string) {
		toast.promise(
			async () => {
				const response = await client.stock["order-templates"][":orderTemplateId"].$get({
					param: { orderTemplateId },
				});
				const data = await responseHandler(response);
				await handleOnAddArticles(data.orderTemplate.articleIds);
			},
			{
				loading: t("general.loading"),
				success: t("stock.successfully_inserted_template_articles"),
				error: t("stock.inserting_template_articles_failed"),
			},
		);
	}

	async function handleOnAddArticles(articleIds: string[]) {
		const current = methods.getValues("articles");
		methods.setValue("articles", mergeArticleIds(current, articleIds));
	}

	/** Render */
	return (
		<Fragment>
			<input {...dropzone.getInputProps()} />
			<Menu
				items={() => [
					{
						label: t("stock.select_articles"),
						icon: icon("List"),
						onClick: disclosure.onOpen,
					},
					{
						label: t("files.import_file"),
						icon: icon("Table"),
						onClick: handleOnOpenFileImport,
					},
					t("stock.order_templates"),
					...guardArray(orderTemplates).map((orderTemplate, index) => ({
						label: orderTemplate.name,
						icon: () => <span className="text-sm">{`${index + 1}. `}</span>,
						onClick: async () => {
							handleOnSelectOrderTemplate(orderTemplate.id);
						},
					})),
				]}
			>
				<Button
					variant="primary"
					icon={<Icon name="Plus" />}
				>
					{t("stock.add_articles")}
				</Button>
			</Menu>
			{disclosure.isOpen && (
				<SelectArticlesModal
					isOpen={disclosure.isOpen}
					onClose={disclosure.onClose}
					initialArticleIds={methods.getValues("articles").map((article) => article.articleId)}
					onComplete={handleOnAddArticles}
					enableRowSelection={(item) => item.hasLocation}
				/>
			)}
			{columnFormController.isOpen && (
				<ConfirmModal
					controller={columnFormController}
					message={null}
					title={t("general.select_columns")}
					onResolve={async () => {
						const isValid = columnFormMethods.trigger();
						if (!isValid) return false;
						return columnFormMethods.getValues();
					}}
					confirmVariant="primary"
					confirmLabel={t("general.select")}
				>
					<ColumnForm methods={columnFormMethods} />
				</ConfirmModal>
			)}
			{importErrorController.isOpen && (
				<ConfirmModal
					controller={importErrorController}
					message={null}
					confirmLabel={t("general.i_understand")}
					confirmButtonProps={{ variant: "primary" }}
					rejectButtonProps={{ className: "hidden" }}
				/>
			)}
		</Fragment>
	);
}
